import apiClient from '../config/api';

const ENDPOINTS = {
    MEMBERS: '/api/members',
    MEMBER_SEARCH: '/api/members/search',
    MEMBER_TIERS: '/api/members/tiers',
    ALLERGIES: '/api/members/allergies',
    ALLERGIES_ORDER: '/api/members/allergies/order',
    REWARDS: '/api/members/rewards',
    REWARDS_ORDER: '/api/members/rewards/order',
};

export const memberService = {
    // Get all members with optional filters and pagination
    getMembers: async (params = {}) => {
        const response = await apiClient.get(ENDPOINTS.MEMBERS, { params });
        return response.data;
    },

    // Search for a member by ID, membership number, or phone
    searchMember: async (searchCriteria) => {
        const response = await apiClient.get(ENDPOINTS.MEMBER_SEARCH, {
            params: { searchCriteria }
        });
        return response.data;
    },

    // Create new member
    createMember: async (memberData) => {
        const response = await apiClient.post(ENDPOINTS.MEMBERS, memberData);
        return response.data;
    },

    // Update member
    updateMember: async (memberData) => {
        const response = await apiClient.put(`${ENDPOINTS.MEMBERS}/${memberData.id}`, memberData);
        return response.data;
    },

    // Get member tiers
    getMemberTiers: async () => {
        const response = await apiClient.get(ENDPOINTS.MEMBER_TIERS);
        return response.data;
    },

    // Create new member tier
    createMemberTier: async (tierData) => {
        const response = await apiClient.post(ENDPOINTS.MEMBER_TIERS, tierData);
        return response.data;
    },

    // Delete member tier
    deleteMemberTier: async (tierPoints) => {
        const response = await apiClient.delete(`${ENDPOINTS.MEMBER_TIERS}/${tierPoints}`);
        return response.data;
    },

    // Get allergies
    getAllergies: async () => {
        const response = await apiClient.get(ENDPOINTS.ALLERGIES);
        return response.data;
    },

    // Create new allergy
    createAllergy: async (allergyData) => {
        const response = await apiClient.post(ENDPOINTS.ALLERGIES, allergyData);
        return response.data;
    },

    // Update allergy order (handles reordering and deletion)
    updateAllergyOrder: async (allergies) => {
        const response = await apiClient.put(ENDPOINTS.ALLERGIES_ORDER, { allergies });
        return response.data;
    },

    // Get member transactions with items and remarks
    getMemberTransactions: async (memberId) => {
        const response = await apiClient.get(`${ENDPOINTS.MEMBERS}/${memberId}/transactions`);
        return response.data;
    },

    // Get rewards
    getRewards: async (activeOnly = false) => {
        const response = await apiClient.get(ENDPOINTS.REWARDS, { 
            params: { activeOnly } 
        });
        return response.data;
    },

    // Create new reward
    createReward: async (rewardData) => {
        const response = await apiClient.post(ENDPOINTS.REWARDS, rewardData);
        return response.data;
    },

    // Update reward order (handles reordering, deletion, and IsActive toggle)
    updateRewardOrder: async (rewards) => {
        const response = await apiClient.put(ENDPOINTS.REWARDS_ORDER, { rewards });
        return response.data;
    },

    // Get member rewards by membership number
    getMemberRewards: async (membershipNumber) => {
        const response = await apiClient.get(`${ENDPOINTS.MEMBERS}/${membershipNumber}/rewards`);
        return response.data;
    },

    // Hand out reward to member
    handoutReward: async (rewardData) => {
        const response = await apiClient.post(`${ENDPOINTS.MEMBERS}/member-rewards`, rewardData);
        return response.data;
    },

    // Get member complementary points by membership number
    getMemberComplementaryPoints: async (membershipNumber) => {
        const response = await apiClient.get(`${ENDPOINTS.MEMBERS}/${membershipNumber}/complementary-points`);
        return response.data;
    },

    // Hand out complementary points to member
    handoutComplementaryPoints: async (pointsData) => {
        const response = await apiClient.post(`${ENDPOINTS.MEMBERS}/complementary-points`, pointsData);
        return response.data;
    },

    // Get next available membership number
    getNextMembershipNumber: async () => {
        const response = await apiClient.get(`${ENDPOINTS.MEMBERS}/next-membership-number`);
        return response.data;
    },

    // Get member picture (returns { url, size } or null if no picture)
    getMemberPicture: async (memberId) => {
        try {
            const response = await apiClient.get(`${ENDPOINTS.MEMBERS}/${memberId}/picture`, {
                responseType: 'blob'
            });
            const blob = response.data;
            const typedBlob = blob.type?.startsWith('image/') ? blob : new Blob([blob], { type: 'image/jpeg' });
            return {
                url: URL.createObjectURL(typedBlob),
                size: typedBlob.size
            };
        } catch (err) {
            if (err.response?.status === 404) return null;
            throw err;
        }
    },

    // Upsert member picture (base64 string)
    upsertMemberPicture: async (memberId, pictureBase64) => {
        const response = await apiClient.post(`${ENDPOINTS.MEMBERS}/${memberId}/picture`, {
            picture: pictureBase64
        });
        return response.data;
    },
};

export default memberService;

